import { Controller } from "@hotwired/stimulus"
import { post, destroy } from "@rails/request.js"
import Hls from "hls.js"

// Live camera streaming + recorded-file (VOD) playback for a JIMI device.
// The backend returns the stream URL right after accepting the turn-on
// command, without waiting for the device to actually confirm execution
// (see design.md decision #6) -- native <video> HLS support is used on
// Safari, hls.js everywhere else.
export default class extends Controller {
  static values = { imei: String, protocol: String }
  static targets = ["video", "liveStatus"]

  connect() {
    this.hls = null
  }

  disconnect() {
    this.#destroyHls()
  }

  async startLive() {
    this.liveStatusTarget.textContent = this.liveStatusTarget.dataset.loadingText || "Iniciando..."

    const response = await post(`/devices/${this.imeiValue}/live_stream`, {
      body: JSON.stringify({ protocol: this.protocolValue }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) {
      this.liveStatusTarget.textContent = "Não foi possível iniciar a transmissão."
      return
    }

    const { url } = await response.json
    this.#play(url)
    this.liveStatusTarget.textContent = "Ao vivo"
  }

  async stopLive() {
    await destroy(`/devices/${this.imeiValue}/live_stream`, {
      body: JSON.stringify({ protocol: this.protocolValue }),
      contentType: "application/json"
    })

    this.#destroyHls()
    this.videoTarget.removeAttribute("src")
    this.liveStatusTarget.textContent = "Transmissão parada."
  }

  async playRecording(event) {
    const file = event.params.file

    const response = await post(`/devices/${this.imeiValue}/vod_playback`, {
      body: JSON.stringify({ protocol: this.protocolValue, file }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) return

    const { url } = await response.json
    this.#play(url)
  }

  #play(url) {
    this.#destroyHls()

    if (this.videoTarget.canPlayType("application/vnd.apple.mpegurl")) {
      this.videoTarget.src = url
      this.videoTarget.play()
      return
    }

    if (Hls.isSupported()) {
      this.hls = new Hls()
      this.hls.loadSource(url)
      this.hls.attachMedia(this.videoTarget)
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => this.videoTarget.play())
    }
  }

  #destroyHls() {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }
}
